import { Intervention, Personnel } from '../../../../shared/types/game';
import { statuts, type StatutPlanningRowBase } from '../../../../config';
import { getPlanningServiceKeyFromBuildingType } from '../../utils/buildingServiceMapping';
import { DEFAULT_PLANNING_NOM, getPlanningLabelForLocalDate } from '../OperationalPlanningModal';
import type { InstanceRow } from '../interventions/instanceModel';
import { isTerminalInterventionStatus } from '../interventions/instanceModel';

/** Helpers extraits de BuildingDetailsTab.tsx. */

/** Identifie le joueur concerné par un message socket intervention. */
export function interventionSocketOwnerId(payload: InstanceRow): number | undefined {
  if (payload.joueur_id != null && Number.isFinite(Number(payload.joueur_id))) {
    return Number(payload.joueur_id);
  }
  if (payload.user_id != null && Number.isFinite(Number(payload.user_id))) {
    return Number(payload.user_id);
  }
  return undefined;
}

export function sortPersonnelInGroup(rows: Personnel[]): Personnel[] {
  return [...rows].sort((x, y) => {
    const ox =
      x.grade_ordre != null && !Number.isNaN(Number(x.grade_ordre)) ? Number(x.grade_ordre) : 999_999;
    const oy =
      y.grade_ordre != null && !Number.isNaN(Number(y.grade_ordre)) ? Number(y.grade_ordre) : 999_999;
    if (ox !== oy) return ox - oy;
    const nx = `${x.nom} ${x.prenom}`.trim();
    const ny = `${y.nom} ${y.prenom}`.trim();
    return nx.localeCompare(ny, 'fr', { sensitivity: 'base' });
  });
}

export function resolvePlanningRowForLabel(
  label: string,
  serviceKey: ReturnType<typeof getPlanningServiceKeyFromBuildingType>
): StatutPlanningRowBase | null {
  const u = label.trim().toUpperCase();
  if (serviceKey) {
    const svcRows = statuts.statuts_planning_by_service[serviceKey];
    const row = svcRows?.find((p) => p.nom.trim().toUpperCase() === u);
    if (row) return row;
  }
  return statuts.statuts_planning.find((p) => p.nom.trim().toUpperCase() === u) ?? null;
}

export function planningBadgeCode(row: StatutPlanningRowBase | null, label: string): string {
  if (row) {
    const c = row.code?.trim();
    if (c) return c;
    return String(row.id);
  }
  const s = label.trim();
  return s.length > 10 ? `${s.slice(0, 8)}…` : s || '—';
}

/** Ordre d'affichage des blocs = `prio` dans `statuts_planning`, `prio` null en fin. */
export function planningGroupSortRank(
  label: string,
  serviceKey: ReturnType<typeof getPlanningServiceKeyFromBuildingType>
): number {
  const u = label.trim().toUpperCase();
  const nullishPrio = 999_999;
  const unknownPrio = 1_000_000;

  const fromRows = (
    rows: ReadonlyArray<{ nom: string; prio: number | null }>
  ): number | null => {
    const row = rows.find((p) => p.nom.trim().toUpperCase() === u);
    if (!row) return null;
    if (row.prio != null && !Number.isNaN(Number(row.prio))) return Number(row.prio);
    return nullishPrio;
  };

  if (serviceKey) {
    const svcRows = statuts.statuts_planning_by_service[serviceKey];
    if (svcRows?.length) {
      const r = fromRows(svcRows);
      if (r !== null) return r;
    }
  }

  const global = statuts.statuts_planning.find((p) => p.nom.trim().toUpperCase() === u);
  if (global) {
    if (global.prio != null && !Number.isNaN(Number(global.prio))) return Number(global.prio);
    return nullishPrio;
  }

  return unknownPrio;
}

export function comparePlanningGroupsByPrio(
  a: string,
  b: string,
  serviceKey: ReturnType<typeof getPlanningServiceKeyFromBuildingType>
): number {
  const pa = planningGroupSortRank(a, serviceKey);
  const pb = planningGroupSortRank(b, serviceKey);
  if (pa !== pb) return pa - pb;
  return a.localeCompare(b, 'fr', { sensitivity: 'base' });
}

/**
 * Rattache une ligne d'intervention à une instance CTA via le dispatch véhicules.
 * Score : priorité au chevauchement explicite `vehicules_ids`, puis engins du bâtiment engagés.
 */
export function resolveCtaInstanceIdForIntervention(
  inter: Intervention,
  ctaInstances: InstanceRow[],
  buildingVehicleIds: Set<number>
): number | null {
  const active = ctaInstances.filter(
    (c) => !isTerminalInterventionStatus(String(c.statut || ''))
  );
  const sorted = [...active].sort((a, b) => {
    const ta = a.date_creation ? new Date(a.date_creation).getTime() : 0;
    const tb = b.date_creation ? new Date(b.date_creation).getTime() : 0;
    return tb - ta;
  });
  let bestId: number | null = null;
  let bestScore = -1;
  for (const cta of sorted) {
    const dispatched = cta.dispatch_player_vehicle_ids ?? [];
    if (!dispatched.length) continue;
    const jsonV = (inter.vehicules_ids ?? []).map((x) => Number(x));
    let score = 0;
    for (const vid of jsonV) {
      if (dispatched.includes(vid)) score += 3;
    }
    if (score === 0) {
      const touchesBuilding = dispatched.some((id) => buildingVehicleIds.has(Number(id)));
      if (touchesBuilding) score = 1;
    }
    if (score > bestScore) {
      bestScore = score;
      bestId = cta.id;
    }
  }
  return bestScore > 0 ? bestId : null;
}

/** Statut planning du jour = PERSONNELS → compte en dispo, pas en dispo départ. */
export function isPlanningPersonnelsJour(p: Personnel, today: Date): boolean {
  const key =
    String(getPlanningLabelForLocalDate(p, today) || DEFAULT_PLANNING_NOM).trim() ||
    DEFAULT_PLANNING_NOM;
  return key.toUpperCase() === DEFAULT_PLANNING_NOM.toUpperCase();
}
